import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { APP_NAME, APP_SUBTITLE, BRAND_COLORS } from '../shared/app_config';
import logo from '../assets/logo.svg';
import { typography } from '~/theme';

interface SplashScreenProps {
  onFinished: () => void;
  duration?: number;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ onFinished, duration = 1800 }) => {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setFadeOut(true), duration);
    const doneTimer = setTimeout(() => onFinished(), duration + 400);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [onFinished, duration]);

  return (
    <Box
      className={fadeOut ? 'splash-fade-out' : ''}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: BRAND_COLORS.dark,
      }}
    >
      {/* Logo */}
      <Box
        className="splash-fade-in"
        sx={{ mb: 4 }}
      >
        <img
          src={logo}
          alt={APP_NAME}
          style={{ width: 100, height: 100, filter: 'drop-shadow(0 4px 20px rgba(156,225,190,0.4))' }}
        />
      </Box>

      {/* App name — gradient text */}
      <Typography
        className="splash-fade-in"
        variant="h3"
        sx={{
          fontWeight: typography.h1.fontWeight,
          letterSpacing: typography.h1.letterSpacing,
          animationDelay: '0.15s',
          opacity: 0,
          animationFillMode: 'forwards',
          background: BRAND_COLORS.gradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {APP_NAME}
      </Typography>

      {/* Subtitle */}
      <Typography
        className="splash-fade-in"
        variant="body1"
        sx={{
          color: 'rgba(255,255,255,0.5)',
          mt: 1,
          fontWeight: typography.body1.fontWeight,
          animationDelay: '0.3s',
          opacity: 0,
          animationFillMode: 'forwards',
        }}
      >
        {APP_SUBTITLE}
      </Typography>

      {/* Loading dots */}
      <Box
        className="splash-fade-in"
        sx={{
          mt: 5,
          display: 'flex',
          gap: 1,
          animationDelay: '0.5s',
          opacity: 0,
          animationFillMode: 'forwards',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            className="splash-pulse"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: BRAND_COLORS.gradient,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SplashScreen;
